/**
 * CourseConfigurePage — /courses/:id/configure
 *
 * Four-step wizard following the Figma "Configure Course (One pager)" design
 * (Admin-Dashboard file, node 11126:514547):
 *
 *   1. Audiences            — group learners into named audiences
 *   2. Time Frames          — per-audience start/end dates
 *   3. Invitation text      — per-audience channel + message (UI-ONLY)
 *   4. Publishing           — per-audience publish dates + final save
 *
 * Endpoints used:
 *   - GET    /courses/{id}                          → load detail (title, sections)
 *   - POST   /courses/{id}/assign                   → one call per audience on save
 *   - GET    /courses/{id}/assignments              → list current audiences
 *   - DELETE /courses/{id}/assignments/{assignment} → remove a draft audience
 *   - PUT    /courses/{id}/assignments/{assignment} → schedule edits
 *   - GET    /learners?per_page=…                   → audience picker
 *   - GET    /learners?role_id|department_id|location_id=… → "Add by …" pickers
 *   - GET    /groups                                → audience picker
 *   - POST   /learners/bulk                         → "Bulk Upload with CSV",
 *            whose imported ids are selected into the audience being built
 *
 * Known modelling gap: the API stores a flat CourseAssignment row per
 * (course, learner), so an "audience" is reconstructed by grouping rows that
 * share a title/schedule (see assignmentGroups.ts).
 */

import { EyeSlashIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { EASE_OUT } from '@/lib/motion';
import { palette } from '@/styles/figma-tokens';
import ComposeText from '@/components/configure/ComposeText';
import ChannelSelect from '@/components/configure/ChannelSelect';
import { useSidebar } from '@/contexts/SidebarContext';
import {
  useCourse,
  useAssignCourse,
  useAssignmentsList,
  useDeleteAssignment, useUpdateCourse } from '../../services/courses';
import { groupAssignments, findGroupByRowId } from './assignmentGroups';
import PublishSwapDialog from '../../components/courses/PublishSwapDialog';
import PublishStatusControl, {
  type PublishableStatus,
} from '../../components/common/PublishStatusControl';
import { useRevokeAssignment, useUpdateAssignment } from '../../services/courses';
import {
  useAllGroups,
  useLearnerRoles,
  useLearnerDepartments,
  useLearnerLocations,
  fetchLearners,
} from '../../services/learners';
import { useAssignableLearners } from '../../hooks/useAssignableLearners';
import type {
  Learner,
  LearnerGroup,
  LearnerRole,
  LearnerDepartment,
  LearnerLocation,
} from '../../types/learner';
import type { AssignChannel } from '../../types/assignment';
import type { WizardStepNumber } from './wizard/wizardTypes';
import StepCard from './wizard/shared/StepCard';
import WizardStepIndicator from './wizard/WizardStepIndicator';
import { useWizardState } from './wizard/useWizardState';
import CourseAssignmentsLanding from './CourseAssignmentsLanding';
import CsvLearnerImport from '@/components/learners/CsvLearnerImport';
import { usePlanFeatures } from '@/hooks/usePlanFeatures';
import { useDismissible } from '@/hooks/learner/useMemberConfig';

// ─── Tokens (pulled from Figma variables for the Configure section) ──────────

const PURPLE = 'rgb(var(--color-primary))';
const ORANGE = 'rgb(var(--color-secondary))';
const FONT = 'Poppins, sans-serif';

const STEP_LABELS: Record<WizardStepNumber, string> = {
  1: 'Audiences',
  2: 'Time Frames',
  3: 'Invitation Text',
  4: 'Publishing',
};

// ─── Audience model (client-only) ────────────────────────────────────────────

interface Audience {
  id: string;
  name: string;
  learnerIds: Set<string>;
  groupIds: Set<string>;
  startDate: string;
  endDate: string;
  publishDate: string;
  /** Channels for the invite ("EMAIL" and/or "CHAT"). */
  invitationChannels: AssignChannel[];
  invitationHtml: string;
}

const newAudience = (): Audience => ({
  id: `aud-${Math.random().toString(36).slice(2, 9)}-${Date.now().toString(36).slice(-3)}`,
  // Name is optional — left blank, the audience falls back to a dated label
  // (see audienceDisplayName). Users can name it via the inline "Update" rename.
  name: '',
  learnerIds: new Set(),
  groupIds: new Set(),
  startDate: '',
  endDate: '',
  publishDate: '',
  invitationChannels: ['EMAIL'],
  invitationHtml: '',
});

/** Format a YYYY-MM-DD string as dd/mm for compact labels. */
function fmtDayMonth(iso: string): string {
  const [, m, d] = iso.split('-');
  return d && m ? `${d}/${m}` : iso;
}

/**
 * Display name for an audience: the explicit name if set, else a dated
 * "dd/mm → dd/mm" range, else an ordinal fallback while it's still blank.
 */
function audienceDisplayName(
  aud: Pick<Audience, 'name' | 'startDate' | 'endDate'>,
  index: number
): string {
  if (aud.name.trim()) return aud.name.trim();
  if (aud.startDate && aud.endDate) return `${fmtDayMonth(aud.startDate)} → ${fmtDayMonth(aud.endDate)}`;
  return `Audience ${index + 1}`;
}

// ─── Inline icons ────────────────────────────────────────────────────────────

const PlusIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const PenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M15.232 5.232l3.536 3.536M16.707 3.293a2 2 0 012.828 0l1.172 1.172a2 2 0 010 2.828L7.5 20.5H3v-4.5L16.707 3.293z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserPlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="2" />
    <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6M19 8v6M16 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UsersIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M17 20v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9.5 8.5a3.5 3.5 0 11-7 0 3.5 3.5 0 017 0zM22 20v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const XIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EyeOpenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

// ─── Reminder card (peach, matches Figma) ────────────────────────────────────
//
// Reminder is a fully-controlled component: the parent owns the dismissed state
// so it can collapse the surrounding layout column the moment the user clicks
// dismiss. Persistence is handled by `useReminderDismiss` → the member's
// `/me/config` (so dismissals carry across devices).

interface ReminderProps {
  title: string;
  body: React.ReactNode;
  onDismiss: () => void;
}

function Reminder({ title, body, onDismiss }: ReminderProps) {
  return (
    <div
      className="relative rounded-2xl px-7 py-7 border h-full"
      style={{
        background: 'rgb(var(--color-secondary) / 0.08)',
        borderColor: 'rgb(var(--color-secondary) / 0.30)',
      }}
    >
      <button
        type="button"
        aria-label="Dismiss reminder"
        onClick={onDismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full border inline-flex items-center justify-center text-text-secondary hover:bg-text-tertiary/5 active:scale-[0.96] transition-[transform,background-color] duration-150"
        style={{ borderColor: 'rgb(var(--color-secondary) / 0.45)', color: ORANGE }}
      >
        <XIcon size={12} />
      </button>
      <h3 className="text-lg font-bold text-text-primary text-center mb-3" style={{ fontFamily: FONT }}>
        {title}
      </h3>
      <div className="text-sm text-text-secondary leading-relaxed text-center" style={{ fontFamily: FONT }}>
        {body}
      </div>
      <div className="flex justify-center mt-5">
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border border-text-primary/30 text-text-primary hover:bg-text-tertiary/5 active:scale-[0.97] transition-[transform,background-color] duration-150"
        >
          <EyeSlashIcon className="w-4 h-4" aria-hidden="true" />
          Never show it again
        </button>
      </div>
    </div>
  );
}

/**
 * Wraps a main+reminder section in a flex layout. When `reminderVisible` flips
 * to false the right column animates `width: 0 + opacity: 0` and the main
 * column reflows to fill the row. Honors `prefers-reduced-motion`.
 */
interface ReminderLayoutProps {
  reminderVisible: boolean;
  reminderNode: React.ReactNode;
  children: React.ReactNode;
}

function ReminderLayout({ reminderVisible, reminderNode, children }: ReminderLayoutProps) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="flex flex-col lg:flex-row gap-6 items-stretch">
      <div className="flex-1 min-w-0">{children}</div>
      <AnimatePresence initial={false}>
        {reminderVisible && (
          <motion.aside
            key="reminder"
            initial={reduceMotion ? false : { opacity: 0 }}
            // Only animate opacity on enter — the column width comes from
            // the Tailwind classes below. Setting `width: 'auto'` here would
            // override `lg:w-[320px]` with the reminder card's natural width
            // and eat into the main column.
            animate={{ opacity: 1 }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, width: 0, marginLeft: -24 /* counters the gap-6 (24px) */ }
            }
            transition={{ duration: 0.25, ease: EASE_OUT }}
            className="w-full lg:w-[320px] shrink-0 overflow-hidden"
          >
            {reminderNode}
          </motion.aside>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Translucent lock overlay for steps gated on a prerequisite (e.g. "no
 * audience yet"). Blocks interaction and surfaces the gating reason.
 */
function LockedOverlay({ message }: { message: string }) {
  return (
    <div className="absolute inset-0 z-20 rounded-2xl bg-background-primary/65 dark:bg-grey-900/65 backdrop-blur-[2px] flex items-center justify-center cursor-not-allowed">
      <span
        className="text-[13px] font-semibold text-text-secondary bg-background-primary border border-border-primary/20 rounded-lg px-3.5 py-1.5 shadow-sm"
        style={{ fontFamily: FONT }}
      >
        {message}
      </span>
    </div>
  );
}

/**
 * Lift Reminder dismissal state up so the parent can collapse the column.
 * Backed by the member's `/me/config`; treat "still loading config" as hidden
 * so a previously-dismissed reminder never flashes before its state is known.
 */
function useReminderDismiss(storageKey: string) {
  const { dismissed, isLoading, dismiss } = useDismissible(storageKey);
  return { dismissed: isLoading || dismissed, dismiss };
}

// ─── Audience tabs ───────────────────────────────────────────────────────────

interface AudienceTabsProps {
  audiences: Audience[];
  activeId: string | null;
  onSelect: (id: string) => void;
  /** When set, renders an extra tab on the right that maps to the same callback */
  extraTab?: { label: string; id: string };
}

function AudienceTabs({ audiences, activeId, onSelect, extraTab }: AudienceTabsProps) {
  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: extraTab ? `repeat(${audiences.length}, 1fr) auto` : `repeat(${audiences.length}, minmax(0, 1fr))` }}>
      {audiences.map((aud, i) => {
        const active = aud.id === activeId;
        return (
          <button
            key={aud.id}
            type="button"
            onClick={() => onSelect(aud.id)}
            className={`h-11 rounded-full text-sm font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.98] ${ active ? 'bg-primary-500 ' : 'bg-text-tertiary/10 dark:bg-text-tertiary/15 text-text-primary' }`}
            style={{ fontFamily: FONT, letterSpacing: '0.04em' }}
          >
            {audienceDisplayName(aud, i).toUpperCase()}
          </button>
        );
      })}
      {extraTab && (
        <button
          type="button"
          onClick={() => onSelect(extraTab.id)}
          className={`h-11 px-5 rounded-full text-sm font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.98] ${ activeId === extraTab.id ? 'bg-primary-500 ' : 'bg-text-tertiary/10 dark:bg-text-tertiary/15 text-text-primary' }`}
          style={{ fontFamily: FONT, letterSpacing: '0.04em' }}
        >
          {extraTab.label}
        </button>
      )}
    </div>
  );
}

// ─── Add Learners modal (4-mode picker, matching Figma) ──────────────────────

type AddLearnersMode = null | 'individuals' | 'role' | 'department' | 'location' | 'csv';

interface AddLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  audience: Audience | null;
  learners: Learner[];
  groups: LearnerGroup[];
  roles: LearnerRole[];
  departments: LearnerDepartment[];
  locations: LearnerLocation[];
  loading: boolean;
  rolesLoading: boolean;
  departmentsLoading: boolean;
  locationsLoading: boolean;
  onApply: (next: { learnerIds: Set<string>; groupIds: Set<string> }) => void;
}

function AddLearnersModal({
  isOpen,
  onClose,
  audience,
  learners,
  groups,
  roles,
  departments,
  locations,
  loading,
  rolesLoading,
  departmentsLoading,
  locationsLoading,
  onApply,
}: AddLearnersModalProps) {
  // Bulk CSV import is a Growth+ feature — hidden below it, exactly as the
  // Bulk Upload button on the learner-create page is.
  const { attributeFilters, bulkTools } = usePlanFeatures();
  const [mode, setMode] = useState<AddLearnersMode>(null);
  const [draftLearners, setDraftLearners] = useState<Set<string>>(new Set());
  const [draftGroups, setDraftGroups] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isOpen || !audience) return;
    setMode(null);
    setSearch('');
    setDraftLearners(new Set(audience.learnerIds));
    setDraftGroups(new Set(audience.groupIds));
  }, [isOpen, audience?.id]);

  const filteredLearners = useMemo(() => {
    if (!search) return learners;
    const q = search.toLowerCase();
    return learners.filter(
      (l) => l.full_name.toLowerCase().includes(q) || l.email.toLowerCase().includes(q)
    );
  }, [learners, search]);

  const filteredGroups = useMemo(() => {
    if (!search) return groups;
    const q = search.toLowerCase();
    return groups.filter((g) => g.name.toLowerCase().includes(q));
  }, [groups, search]);

  if (!audience) return null;

  const totalSelected = draftLearners.size + draftGroups.size;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="bd"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: EASE_OUT }}
            className="fixed inset-0 z-40 bg-black/50"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
            <motion.div
              key="card"
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.18, ease: EASE_OUT }}
              className="pointer-events-auto bg-background-primary rounded-2xl w-full max-w-[480px] max-h-[88vh] overflow-y-auto"
              style={{ boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.18)' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Add learners"
            >
              <motion.div
                key={mode ?? 'menu'}
                initial={reduceMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.12, ease: EASE_OUT }}
                className="px-7 py-7"
              >
                {mode === null && (
                  <>
                    <h2 className="text-xl font-bold text-text-primary text-center mb-5" style={{ fontFamily: FONT }}>
                      Add Learners
                    </h2>
                    <div className="flex flex-col gap-3">
                      <ModeRow
                        label="Add individuals"
                        variant="purple"
                        icon={<PlusIcon />}
                        onClick={() => setMode('individuals')}
                      />
                      {/* Attribute shortcuts are the paid "Attribute filters"
                          row (Growth+) — the learner list gates them the same
                          way, so Free cannot reach them here either. */}
                      {attributeFilters && (
                        <>
                          <ModeRow
                            label="Add by Role"
                            variant="purple"
                            icon={<PlusIcon />}
                            onClick={() => setMode('role')}
                          />
                          <ModeRow
                            label="Add by Department"
                            variant="purple"
                            icon={<PlusIcon />}
                            onClick={() => setMode('department')}
                          />
                          <ModeRow
                            label="Add by Location"
                            variant="purple"
                            icon={<PlusIcon />}
                            onClick={() => setMode('location')}
                          />
                        </>
                      )}
                      {bulkTools && (
                      <ModeRow
                        label="Bulk Upload with CSV"
                        variant="dark"
                        icon={<UploadIcon />}
                        onClick={() => setMode('csv')}
                      />
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-4 mt-6">
                      <PillButton onClick={onClose} variant="orange-outline">
                        <XIcon size={14} /> Cancel
                      </PillButton>
                    </div>
                  </>
                )}

                {mode === 'individuals' && (
                  <>
                    <button
                      type="button"
                      onClick={() => setMode(null)}
                      className="text-xs font-semibold text-text-secondary hover:text-text-primary active:scale-[0.97] transition-[color,transform] duration-150 mb-3"
                    >
                      ← Back
                    </button>
                    <h2 className="text-xl font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>
                      Add individuals
                    </h2>
                    <div className="relative mb-3">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none">
                        <SearchIcon />
                      </span>
                      <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Find learners or groups…"
                        className="w-full h-10 pl-9 pr-3 text-sm bg-background-primary border border-border-primary/25 rounded-xl outline-none placeholder:text-text-tertiary text-text-primary focus:border-primary-500 transition-colors"
                      />
                    </div>
                    <div className="rounded-xl border border-border-primary/20 max-h-[320px] overflow-y-auto">
                      {loading ? (
                        <div className="p-3 space-y-2">
                          {[1, 2, 3].map((i) => (
                            <div key={i} className="h-9 rounded-lg bg-text-tertiary/10 animate-pulse" />
                          ))}
                        </div>
                      ) : filteredLearners.length === 0 && filteredGroups.length === 0 ? (
                        <p className="px-4 py-6 text-center text-sm text-text-secondary">
                          {search ? 'No matches.' : 'No learners or groups yet.'}
                        </p>
                      ) : (
                        <ul className="divide-y divide-border-primary/10">
                          {filteredGroups.map((g) => {
                            const selected = draftGroups.has(g.id);
                            return (
                              <li key={`g-${g.id}`}>
                                <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-text-tertiary/5 transition-colors">
                                  <Checkbox checked={selected} />
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={selected}
                                    onChange={() => {
                                      const next = new Set(draftGroups);
                                      next.has(g.id) ? next.delete(g.id) : next.add(g.id);
                                      setDraftGroups(next);
                                    }}
                                  />
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-semibold text-text-primary truncate">
                                      {g.name}
                                    </span>
                                    <span className="block text-xs text-text-secondary">
                                      Group · {g.members_count} {g.members_count === 1 ? 'learner' : 'learners'}
                                    </span>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                          {filteredLearners.map((l) => {
                            const selected = draftLearners.has(l.id);
                            return (
                              <li key={`l-${l.id}`}>
                                <label className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-text-tertiary/5 transition-colors">
                                  <Checkbox checked={selected} />
                                  <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={selected}
                                    onChange={() => {
                                      const next = new Set(draftLearners);
                                      next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                                      setDraftLearners(next);
                                    }}
                                  />
                                  <span className="flex-1 min-w-0">
                                    <span className="block text-sm font-semibold text-text-primary truncate">
                                      {l.full_name}
                                    </span>
                                    <span className="block text-xs text-text-secondary truncate">
                                      {l.email}
                                    </span>
                                  </span>
                                </label>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4 mt-5">
                      <p className="text-xs text-text-secondary">
                        <span className="font-bold text-text-primary">{totalSelected}</span> selected
                      </p>
                      <div className="flex items-center gap-3">
                        <PillButton onClick={onClose} variant="orange-outline">
                          <XIcon size={14} /> Cancel
                        </PillButton>
                        <PillButton
                          onClick={() => {
                            onApply({ learnerIds: draftLearners, groupIds: draftGroups });
                            onClose();
                          }}
                          variant="purple"
                        >
                          <CheckIcon size={14} /> Save
                        </PillButton>
                      </div>
                    </div>
                  </>
                )}

                {(mode === 'role' || mode === 'department' || mode === 'location') && (
                  <AttributePicker
                    type={mode}
                    attributes={
                      mode === 'role' ? roles : mode === 'department' ? departments : locations
                    }
                    loading={
                      mode === 'role'
                        ? rolesLoading
                        : mode === 'department'
                          ? departmentsLoading
                          : locationsLoading
                    }
                    onAddLearners={(ids) => {
                      const next = new Set(draftLearners);
                      ids.forEach((id) => next.add(id));
                      setDraftLearners(next);
                    }}
                    onBack={() => setMode(null)}
                    onDone={() => {
                      onApply({ learnerIds: draftLearners, groupIds: draftGroups });
                      onClose();
                    }}
                    onCancel={onClose}
                  />
                )}

                {mode === 'csv' && (
                  <CsvLearnerImport
                    onBack={() => setMode(null)}
                    onImported={(ids) => {
                      const next = new Set(draftLearners);
                      ids.forEach((id) => next.add(id));
                      setDraftLearners(next);
                      setMode('individuals');
                    }}
                  />
                )}
              </motion.div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// ─── Attribute picker (Add by Role / Department / Location) ─────────────────

interface AttributePickerProps {
  type: 'role' | 'department' | 'location';
  attributes: Array<LearnerRole | LearnerDepartment | LearnerLocation>;
  loading: boolean;
  onAddLearners: (learnerIds: string[]) => void;
  onBack: () => void;
  onDone: () => void;
  onCancel: () => void;
}

function AttributePicker({
  type,
  attributes,
  loading,
  onAddLearners,
  onBack,
  onDone,
  onCancel,
}: AttributePickerProps) {
  const [adding, setAdding] = useState<string | null>(null);
  const [addedIds, setAddedIds] = useState<Set<string>>(new Set());
  const reduceMotion = useReducedMotion();

  const handleAdd = async (attrId: string) => {
    setAdding(attrId);
    try {
      const result = await fetchLearners({
        per_page: 100,
        ...(type === 'role'
          ? { role_id: attrId }
          : type === 'department'
            ? { department_id: attrId }
            : { location_id: attrId }),
      });
      const ids = result.learners.map((l) => l.id);
      onAddLearners(ids);
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.add(attrId);
        return next;
      });
      toast.success(
        `Added ${ids.length} ${ids.length === 1 ? 'learner' : 'learners'}`
      );
    } catch {
      toast.error('Could not load learners for this ' + type + '.');
    } finally {
      setAdding(null);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="text-xs font-semibold text-text-secondary hover:text-text-primary active:scale-[0.97] transition-[color,transform] duration-150 mb-3"
      >
        ← Back
      </button>
      <h2 className="text-xl font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>
        {type === 'role'
          ? 'Add by Role'
          : type === 'department'
            ? 'Add by Department'
            : 'Add by Location'}
      </h2>

      <div className="rounded-xl border border-border-primary/20 max-h-[320px] overflow-y-auto">
        {loading ? (
          <div className="p-3 space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 rounded-lg bg-text-tertiary/10 animate-pulse" />
            ))}
          </div>
        ) : attributes.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
            No {type === 'role' ? 'roles' : type === 'department' ? 'departments' : 'locations'}{' '}
            defined yet.
            <br />
            <span className="text-xs text-text-tertiary">
              Add them in Settings → Learners → {type === 'role' ? 'Roles' : 'Locations'}.
            </span>
          </p>
        ) : (
          <ul className="divide-y divide-border-primary/10">
            {attributes.map((attr, i) => {
              const added = addedIds.has(attr.id);
              const isAdding = adding === attr.id;
              return (
                <motion.li
                  key={attr.id}
                  initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18, ease: EASE_OUT, delay: reduceMotion ? 0 : i * 0.035 }}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-text-primary truncate" style={{ fontFamily: FONT }}>
                      {attr.name}
                    </span>
                    <span className="block text-xs text-text-secondary">
                      {attr.learner_count} {attr.learner_count === 1 ? 'learner' : 'learners'}
                      {attr.description ? ` · ${attr.description}` : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleAdd(attr.id)}
                    disabled={isAdding || added || attr.learner_count === 0}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-[background-color,color,transform] duration-150 active:scale-[0.97] disabled:active:scale-100 ${ added ? 'bg-success-500/10 text-success-500 border border-success-500/30 cursor-default' : 'bg-primary-500 hover:brightness-105 disabled:opacity-50' }`}
                    style={{ fontFamily: FONT }}
                  >
                    {isAdding ? (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="animate-spin" aria-hidden="true">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="3" />
                          <path d="M12 3a9 9 0 019 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                        </svg>
                        Adding…
                      </>
                    ) : added ? (
                      <>
                        <CheckIcon size={11} /> Added
                      </>
                    ) : (
                      <>
                        <PlusIcon size={11} /> Add
                      </>
                    )}
                  </button>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="flex items-center justify-end gap-3 mt-5">
        <PillButton onClick={onCancel} variant="orange-outline">
          <XIcon size={14} /> Cancel
        </PillButton>
        <PillButton onClick={onDone} variant="purple">
          <CheckIcon size={14} /> Done
        </PillButton>
      </div>
    </>
  );
}

// ─── Audience Preview modal ──────────────────────────────────────────────────

interface AudiencePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  audiences: Audience[];
  learners: Learner[];
  groups: LearnerGroup[];
}

function AudiencePreviewModal({
  isOpen,
  onClose,
  audiences,
  learners,
  groups,
}: AudiencePreviewModalProps) {
  const reduceMotion = useReducedMotion();
  const learnerById = useMemo(() => {
    const map = new Map<string, Learner>();
    learners.forEach((l) => map.set(l.id, l));
    return map;
  }, [learners]);

  const groupById = useMemo(() => {
    const map = new Map<string, LearnerGroup>();
    groups.forEach((g) => map.set(g.id, g));
    return map;
  }, [groups]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="bd"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: EASE_OUT }}
            className="fixed inset-0 z-40 bg-black/55"
            onClick={onClose}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
            <motion.div
              key="card"
              initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.18, ease: EASE_OUT }}
              className="pointer-events-auto bg-background-primary rounded-2xl w-full max-w-[640px] max-h-[88vh] overflow-y-auto"
              style={{ boxShadow: '4px 7px 28px 0px rgba(0,0,0,0.18)' }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Audience Preview"
            >
              <div className="px-7 py-7">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-text-primary" style={{ fontFamily: FONT }}>
                    Audience Preview
                  </h2>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close preview"
                    className="w-8 h-8 rounded-full inline-flex items-center justify-center text-text-secondary hover:bg-text-primary/[0.06] active:scale-[0.94] transition-[background-color,transform] duration-150"
                  >
                    <XIcon size={14} />
                  </button>
                </div>

                {audiences.length === 0 ? (
                  <p className="text-sm text-text-secondary text-center py-6">
                    No audiences yet.
                  </p>
                ) : (
                  <div className="flex flex-col gap-5">
                    {audiences.map((aud, i) => {
                      const selectedGroups = Array.from(aud.groupIds)
                        .map((id) => groupById.get(id))
                        .filter((g): g is LearnerGroup => Boolean(g));
                      const selectedLearners = Array.from(aud.learnerIds)
                        .map((id) => learnerById.get(id))
                        .filter((l): l is Learner => Boolean(l));
                      const totalGroupMembers = selectedGroups.reduce(
                        (sum, g) => sum + (g.members_count ?? 0),
                        0
                      );

                      return (
                        <motion.section
                          key={aud.id}
                          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.18, ease: EASE_OUT, delay: reduceMotion ? 0 : i * 0.04 }}
                          className="rounded-xl border border-border-primary/15 px-5 py-4"
                        >
                          <header className="flex items-baseline justify-between mb-3">
                            <h3
                              className="text-sm font-extrabold text-text-primary tracking-wide"
                              style={{ fontFamily: FONT }}
                            >
                              {audienceDisplayName(aud, i).toUpperCase()}
                            </h3>
                            <span className="text-xs text-text-secondary">
                              {selectedLearners.length + totalGroupMembers} learner
                              {selectedLearners.length + totalGroupMembers === 1 ? '' : 's'}
                            </span>
                          </header>

                          {selectedGroups.length === 0 && selectedLearners.length === 0 ? (
                            <p className="text-xs text-text-tertiary italic">
                              No learners assigned to this audience yet.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-3">
                              {selectedGroups.length > 0 && (
                                <div>
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-text-tertiary mb-1.5">
                                    Groups · {selectedGroups.length}
                                  </p>
                                  <ul className="flex flex-wrap gap-1.5">
                                    {selectedGroups.map((g) => (
                                      <li
                                        key={g.id}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-primary-500/10 text-primary-600 dark:text-primary-300"
                                      >
                                        <span className="font-semibold">{g.name}</span>
                                        <span className="text-text-tertiary">
                                          · {g.members_count}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {selectedLearners.length > 0 && (
                                <div>
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-text-tertiary mb-1.5">
                                    Individual learners · {selectedLearners.length}
                                  </p>
                                  <ul className="flex flex-col">
                                    {selectedLearners.map((l) => (
                                      <li
                                        key={l.id}
                                        className="flex items-baseline justify-between gap-3 py-1 text-xs"
                                      >
                                        <span className="font-semibold text-text-primary truncate">
                                          {l.full_name}
                                        </span>
                                        <span className="text-text-tertiary truncate">
                                          {l.email}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </motion.section>
                      );
                    })}
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <PillButton onClick={onClose} variant="purple">
                    <CheckIcon size={14} /> Done
                  </PillButton>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

function ModeRow({
  label,
  variant,
  icon,
  onClick,
}: {
  label: string;
  variant: 'purple' | 'dark';
  icon: React.ReactNode;
  onClick: () => void;
}) {
  const purple = variant === 'purple';
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-12 rounded-full text-sm font-bold flex items-center justify-center transition-[filter,transform] duration-150 hover:brightness-105 active:scale-[0.98]"
      style={{
        background: purple
          ? `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`
          : palette.grey['900'],
        color: '#fff',
        fontFamily: FONT,
      }}
    >
      <span>{label}</span>
      <span
        className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full inline-flex items-center justify-center"
        style={{
          background: purple ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
        }}
      >
        {icon}
      </span>
    </button>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-md border text-white transition-[background-color,border-color] duration-150 shrink-0"
      style={{
        background: checked ? PURPLE : 'transparent',
        borderColor: checked ? PURPLE : 'rgba(189,188,201,0.5)',
      }}
    >
      {checked && <CheckIcon size={11} />}
    </span>
  );
}

// ─── Pill button (matches Figma cancel/save/save+done style) ────────────────

interface PillButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  /**
   * Optional mousedown handler — useful when a sibling input is focused
   * and we need to call `e.preventDefault()` to keep it focused (so its
   * onBlur doesn't fire and pre-empt this click).
   */
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: 'purple' | 'dark' | 'green' | 'orange-outline' | 'ghost';
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
}

function PillButton({ children, onClick, onMouseDown, variant = 'purple', type = 'button', disabled, className = '' }: PillButtonProps) {
  const base =
    'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-[filter,background-color,transform,color] duration-150 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed';

  // Static (theme-aware) classes per variant. The purple gradient remains
  // inline because Tailwind doesn't ship a 1:1 gradient utility for this exact
  // brand stop pattern.
  const variantClasses: Record<NonNullable<PillButtonProps['variant']>, string> = {
    purple: 'text-white hover:brightness-105',
    dark: 'bg-grey-900 dark:bg-grey-100 text-white dark:text-grey-900 hover:brightness-105',
    green: 'bg-success-500 text-white hover:brightness-105',
    'orange-outline': 'bg-transparent text-secondary border border-secondary hover:bg-secondary/5',
    ghost: 'bg-transparent text-text-primary border border-text-primary/30 hover:bg-text-primary/[0.06]',
  };

  const inlineStyle: React.CSSProperties = { fontFamily: FONT };
  if (variant === 'purple') {
    inlineStyle.background = `linear-gradient(to top, ${'rgb(var(--color-primary-700))'}, rgb(var(--color-primary)) 70%)`;
  }

  return (
    <button
      type={type}
      onClick={onClick}
      onMouseDown={onMouseDown}
      disabled={disabled}
      className={`${base} ${variantClasses[variant]} ${className}`}
      style={inlineStyle}
    >
      {children}
    </button>
  );
}

// ─── Step 1: Audiences ───────────────────────────────────────────────────────

interface Step1AudiencesProps {
  audiences: Audience[];
  onAddAudience: () => void;
  onUpdateAudience: (id: string) => void;
  onAddLearners: (id: string) => void;
}

interface Step1AudiencesPropsExtended extends Step1AudiencesProps {
  /** Directory used to name the members shown on each card. */
  learners: Learner[];
  groups: LearnerGroup[];
  onRenameAudience: (id: string, name: string) => void;
  onRemoveAudience: (id: string) => void;
  onRemoveLearner: (audienceId: string, learnerId: string) => void;
  onRemoveGroup: (audienceId: string, groupId: string) => void;
}

function Step1Audiences({
  audiences,
  learners,
  groups,
  onAddAudience,
  onAddLearners,
  onRenameAudience,
  onRemoveAudience,
  onRemoveLearner,
  onRemoveGroup,
}: Step1AudiencesPropsExtended) {
  const { dismissed, dismiss } = useReminderDismiss('configure-reminder-step1-dismissed');
  return (
    <ReminderLayout
      reminderVisible={!dismissed}
      reminderNode={
        <Reminder
          title="Reminder"
          onDismiss={dismiss}
          body={
            <>
              <p>
                Audiences give you possibilities to launch the course to different
                learner groups at different times with different end dates.
              </p>
              <p className="mt-3">
                Imagine, if you want to train learners of two different roles at
                different times, you will create two different audiences for those
                groups of learners and manage accordingly.
              </p>
            </>
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        <div
          className="rounded-2xl border-2 border-dashed border-primary-500 px-5 py-5 min-h-[200px] flex flex-col gap-3"
          style={{ borderStyle: 'dashed' }}
        >
          {audiences.length === 0 ? (
            <div
              className="flex flex-col items-center gap-1 py-10 text-center"
              style={{ fontFamily: FONT }}
            >
              <p className="text-sm font-semibold text-text-primary">
                No audiences yet
              </p>
              <p className="text-xs text-text-secondary max-w-[44ch]">
                Create your first audience below to start choosing which learners
                receive this course and when.
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              <AnimatePresence initial={false}>
                {audiences.map((aud, i) => (
                  <AudienceCard
                    learners={learners}
                    groups={groups}
                    onRemoveLearner={(learnerId) => onRemoveLearner(aud.id, learnerId)}
                    onRemoveGroup={(groupId) => onRemoveGroup(aud.id, groupId)}
                    key={aud.id}
                    audience={aud}
                    index={i}
                    onAddLearners={() => onAddLearners(aud.id)}
                    onRename={(name) => onRenameAudience(aud.id, name)}
                    onRemove={() => onRemoveAudience(aud.id)}
                  />
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>

        {/* Single page-level action — per-audience controls live inside the cards */}
        <div className="flex justify-center">
          <PillButton variant="purple" onClick={onAddAudience}>
            <PlusIcon size={14} /> Add Audience
          </PillButton>
        </div>
      </div>
    </ReminderLayout>
  );
}

// ─── AudienceCard ───────────────────────────────────────────────────────────

/** Chips shown before the list folds behind a "+N more" toggle. */
const MEMBER_CHIP_LIMIT = 8;

interface AudienceCardProps {
  learners: Learner[];
  groups: LearnerGroup[];
  onRemoveLearner: (learnerId: string) => void;
  onRemoveGroup: (groupId: string) => void;
  audience: Audience;
  index: number;
  onAddLearners: () => void;
  onRename: (name: string) => void;
  onRemove: () => void;
}

function AudienceCard({
  audience,
  index,
  learners,
  groups,
  onAddLearners,
  onRename,
  onRemove,
  onRemoveLearner,
  onRemoveGroup,
}: AudienceCardProps) {
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(audience.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const reduceMotion = useReducedMotion();

  // Keep draft in sync if the parent renames the audience externally.
  useEffect(() => {
    if (!editing) setDraftName(audience.name);
  }, [audience.name, editing]);

  // Auto-focus + select-all when entering rename mode.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== audience.name) onRename(trimmed);
    else setDraftName(audience.name);
    setEditing(false);
  };

  const cancel = () => {
    setDraftName(audience.name);
    setEditing(false);
  };

  const empty = audience.learnerIds.size === 0 && audience.groupIds.size === 0;

  // Groups first, then people — both resolved to names for display. An id with
  // no match (a learner removed from the workspace mid-draft) still renders, so
  // the trainer can see and clear it rather than carrying an invisible member.
  const members = useMemo<{ kind: 'group' | 'learner'; id: string; name: string }[]>(() => {
    const groupById = new Map(groups.map((g) => [g.id, g]));
    const learnerById = new Map(learners.map((l) => [l.id, l]));
    return [
      ...Array.from(audience.groupIds).map((id) => ({
        kind: 'group' as const,
        id,
        name: groupById.get(id)?.name ?? 'Unknown group',
      })),
      ...Array.from(audience.learnerIds).map((id) => {
        const l = learnerById.get(id);
        return {
          kind: 'learner' as const,
          id,
          name: l ? l.full_name || `${l.first_name} ${l.last_name}`.trim() || l.email : 'Unknown learner',
        };
      }),
    ];
  }, [audience.groupIds, audience.learnerIds, groups, learners]);

  const visibleMembers = showAllMembers ? members : members.slice(0, MEMBER_CHIP_LIMIT);
  const hiddenCount = showAllMembers ? 0 : members.length - visibleMembers.length;

  return (
    <motion.li
      layout
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.98 }}
      transition={{ duration: 0.22, ease: EASE_OUT, delay: reduceMotion ? 0 : index * 0.04 }}
      className="group rounded-xl bg-text-tertiary/10 dark:bg-text-tertiary/15 hover:bg-text-tertiary/15 dark:hover:bg-text-tertiary/20 transition-[background-color] duration-150"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 px-5 py-4">
        {/* Identity */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <input
              ref={inputRef}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commit();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancel();
                }
              }}
              maxLength={60}
              aria-label="Audience name"
              placeholder="Name (optional)"
              className="w-full bg-background-primary border border-primary-500/50 rounded-lg px-3 py-1.5 text-base font-extrabold text-text-primary outline-none focus:border-primary-500 transition-colors"
              style={{ fontFamily: FONT, letterSpacing: '0.02em' }}
            />
          ) : (
            <p
              className="text-base font-extrabold text-text-primary leading-6 truncate"
              style={{ fontFamily: FONT, letterSpacing: '0.02em' }}
            >
              {audienceDisplayName(audience, index).toUpperCase()}
            </p>
          )}
          <p
            className={`text-xs mt-1 ${empty ? 'text-text-tertiary italic' : 'text-text-secondary'}`}
            style={{ fontFamily: FONT }}
          >
            {audienceSummary(audience)}
          </p>

          {/* Who is actually in this audience — visible and removable in one
              click, instead of hunting for ticked boxes inside the picker. */}
          {members.length > 0 && (
            <ul className="mt-2.5 flex flex-wrap gap-1.5">
              {visibleMembers.map((m) => (
                <li key={`${m.kind}-${m.id}`}>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-xs font-semibold ${
                      m.kind === 'group'
                        ? 'bg-secondary-500/15 text-secondary-700 dark:text-secondary-300'
                        : 'bg-primary-500/10 text-primary-700 dark:text-primary-300'
                    }`}
                  >
                    {m.kind === 'group' && <UsersIcon />}
                    <span className="max-w-[180px] truncate">{m.name}</span>
                    <button
                      type="button"
                      onClick={() =>
                        m.kind === 'group' ? onRemoveGroup(m.id) : onRemoveLearner(m.id)
                      }
                      aria-label={`Remove ${m.name} from this audience`}
                      title={`Remove ${m.name}`}
                      className="flex h-4 w-4 items-center justify-center rounded-full transition-colors hover:bg-text-primary/15 focus-ring"
                    >
                      <XIcon size={10} />
                    </button>
                  </span>
                </li>
              ))}
              {hiddenCount > 0 && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowAllMembers(true)}
                    className="rounded-full px-2.5 py-1 text-xs font-bold text-text-secondary underline underline-offset-2 hover:text-text-primary focus-ring transition-colors"
                  >
                    +{hiddenCount} more
                  </button>
                </li>
              )}
              {showAllMembers && members.length > MEMBER_CHIP_LIMIT && (
                <li>
                  <button
                    type="button"
                    onClick={() => setShowAllMembers(false)}
                    className="rounded-full px-2.5 py-1 text-xs font-bold text-text-secondary underline underline-offset-2 hover:text-text-primary focus-ring transition-colors"
                  >
                    Show less
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Per-card actions */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <PillButton variant="green" onClick={onAddLearners}>
            <UserPlusIcon /> {editing ? "Modify Learners" : "Add Learners"}
          </PillButton>
          <PillButton
            variant="dark"
            onClick={() => (editing ? commit() : setEditing(true))}
            // While editing, keep input focus on mousedown so its onBlur
            // doesn't fire mid-click (which would re-render → click handler
            // re-reads `editing=false` from the new closure → bounce back
            // into edit mode). With focus retained, only this onClick runs.
            onMouseDown={editing ? (e) => e.preventDefault() : undefined}
          >
            {editing ? <CheckIcon size={13} /> : <PenIcon />}
            {/* "Edit" used to mean "rename", which is why removing a learner
                was so hard to find — the chips above now own that job. */}
            {editing ? 'Save' : 'Rename'}
          </PillButton>
          <button
            type="button"
            aria-label={`Remove ${audienceDisplayName(audience, index)}`}
            onClick={() => {
              if (window.confirm(`Remove ${audienceDisplayName(audience, index)} and its invitations draft?`)) {
                onRemove();
              }
            }}
            title="Remove audience"
            className="inline-flex items-center justify-center w-8 h-8 rounded-full text-text-disabled hover:text-error-500 hover:bg-error-500/10 active:scale-[0.92] transition-[background-color,color,transform] duration-150 opacity-0 group-hover:opacity-100 focus:opacity-100"
          >
            <TrashSmallIcon />
          </button>
        </div>
      </div>
    </motion.li>
  );
}

function TrashSmallIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14.74 9l-.346 9m-4.788 0L9.26 9M5 6h14m-2 0l-1.5 14h-7L4 6m5 0V4a1 1 0 011-1h4a1 1 0 011 1v2"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function audienceSummary(aud: Audience): string {
  const totalLearners = aud.learnerIds.size;
  const totalGroups = aud.groupIds.size;
  if (totalLearners === 0 && totalGroups === 0) return 'No learners yet';
  const parts: string[] = [];
  if (totalGroups > 0) parts.push(`${totalGroups} ${totalGroups === 1 ? 'group' : 'groups'}`);
  if (totalLearners > 0) parts.push(`${totalLearners} ${totalLearners === 1 ? 'learner' : 'learners'}`);
  return parts.join(', ');
}

// ─── Step 2: Time Frames ─────────────────────────────────────────────────────

interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  min?: string;
  max?: string;
  onChange: (next: string) => void;
}

function DateField({ id, label, value, min, max, onChange }: DateFieldProps) {
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="absolute -top-2 left-4 px-1 text-xs font-semibold text-text-primary bg-background-primary z-10"
        style={{ fontFamily: FONT }}
      >
        {label}
      </label>
      <input
        id={id}
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 px-4 rounded-2xl border border-text-primary/30 bg-background-primary text-text-primary outline-none focus:border-primary-500 transition-colors text-sm"
        style={{ fontFamily: FONT }}
      />
    </div>
  );
}

interface Step2TimeFramesProps {
  audiences: Audience[];
  activeId: string;
  setActiveId: (id: string) => void;
  patchAudience: (id: string, patch: Partial<Audience>) => void;
}

function Step2TimeFrames({ audiences, activeId, setActiveId, patchAudience }: Step2TimeFramesProps) {
  const active = audiences.find((a) => a.id === activeId);
  if (!active) return null;
  return (
    <div className="flex flex-col gap-6">
      <p className="text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
        Click on an Audience, set time frames, click Done, then move to the other one.
      </p>

      <AudienceTabs audiences={audiences} activeId={activeId} onSelect={setActiveId} />

      <div
        className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-7"
        style={{ borderStyle: 'dashed' }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <DateField
            id={`start-${active.id}`}
            label="Start date"
            value={active.startDate}
            onChange={(v) => patchAudience(active.id, { startDate: v })}
          />
          <DateField
            id={`end-${active.id}`}
            label="End date"
            value={active.endDate}
            min={active.startDate || undefined}
            onChange={(v) => patchAudience(active.id, { endDate: v })}
          />
        </div>
        <p className="mt-4 text-[13px] leading-5 text-text-secondary">
          The end date is for visibility and reporting only — learners can
          still complete the course after it passes (e.g. after a leave).
        </p>
      </div>
    </div>
  );
}

// ─── Step 3: Invitation text (UI-only — API gap) ─────────────────────────────

interface Step3InvitationProps {
  audiences: Audience[];
  activeId: string;
  setActiveId: (id: string) => void;
  patchAudience: (id: string, patch: Partial<Audience>) => void;
  sameForEveryone: boolean;
  setSameForEveryone: (v: boolean) => void;
}

function Step3Invitation({
  audiences,
  activeId,
  setActiveId,
  patchAudience,
  sameForEveryone,
  setSameForEveryone,
}: Step3InvitationProps) {
  const active = audiences.find((a) => a.id === activeId);
  const { dismissed, dismiss } = useReminderDismiss('configure-reminder-step3-dismissed');
  if (!active) return null;
  const SAME_ID = '__same__';
  const showSameTab = audiences.length > 1;
  return (
    <ReminderLayout
      reminderVisible={!dismissed}
      reminderNode={
        <Reminder
          title="Reminder"
          onDismiss={dismiss}
          body={
            <p>
              The invitation text will be sent the day before the course start
              date. If the start date is Monday, then it will be sent on Friday
              previous week.
            </p>
          }
        />
      }
    >
      <div className="flex flex-col gap-5">
        <div
          className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-6"
          style={{ borderStyle: 'dashed' }}
        >
          <h3 className="text-base font-bold text-text-primary text-center mb-4" style={{ fontFamily: FONT }}>
            Choose Audience
          </h3>
          <AudienceTabs
            audiences={audiences}
            activeId={showSameTab && sameForEveryone ? SAME_ID : activeId}
            onSelect={(id) => {
              if (id === SAME_ID) {
                setSameForEveryone(true);
              } else {
                setSameForEveryone(false);
                setActiveId(id);
              }
            }}
            extraTab={showSameTab ? { label: 'SEND THE SAME TEXT TO EVERYONE', id: SAME_ID } : undefined}
          />
        </div>

        <div className="rounded-2xl border border-text-primary/15 px-6 py-5 bg-text-tertiary/5">
          <h3 className="text-base font-bold text-text-primary text-center mb-1" style={{ fontFamily: FONT }}>
            How Should Learners Be Notified?
          </h3>
          <p className="text-xs text-text-secondary text-center mb-4" style={{ fontFamily: FONT }}>
            Pick at least one channel. Chat posts an invitation card in a group conversation.
          </p>
          <ChannelSelect
            value={active.invitationChannels}
            onChange={(channels) => patchAudience(active.id, { invitationChannels: channels })}
          />
        </div>

        <ComposeText
          value={active.invitationHtml}
          onChange={(html) => patchAudience(active.id, { invitationHtml: html })}
          collapsible
          collapsedLabel="Compose an Invitation Text"
          collapsedHint="Optional — click to personalize the message your learners receive"
        />
      </div>
    </ReminderLayout>
  );
}


// ─── Step 4: Publishing ──────────────────────────────────────────────────────

interface Step4PublishingProps {
  audiences: Audience[];
  activeId: string;
  setActiveId: (id: string) => void;
  patchAudience: (id: string, patch: Partial<Audience>) => void;
  sameForEveryone: boolean;
  setSameForEveryone: (v: boolean) => void;
  setAllPublishDates: (date: string) => void;
}

function Step4Publishing({
  audiences,
  activeId,
  setActiveId,
  patchAudience,
  sameForEveryone,
  setSameForEveryone,
  setAllPublishDates,
}: Step4PublishingProps) {
  const SAME_ID = '__same__';
  const active = audiences.find((a) => a.id === activeId);
  const sameValue = audiences[0]?.publishDate ?? '';
  const showSameTab = audiences.length > 1;
  const { dismissed, dismiss } = useReminderDismiss('configure-reminder-step4-dismissed');
  if (!active) return null;
  return (
    <div className="flex flex-col gap-6">
      <p className="text-center text-sm text-text-secondary" style={{ fontFamily: FONT }}>
        Click on an Audience, set the date, click Done and move to the other one.
      </p>

      <AudienceTabs
        audiences={audiences}
        activeId={showSameTab && sameForEveryone ? SAME_ID : activeId}
        onSelect={(id) => {
          if (id === SAME_ID) {
            setSameForEveryone(true);
          } else {
            setSameForEveryone(false);
            setActiveId(id);
          }
        }}
        extraTab={showSameTab ? { label: 'SET THE SAME DATE FOR EVERYONE', id: SAME_ID } : undefined}
      />

      <ReminderLayout
        reminderVisible={!dismissed}
        reminderNode={
          <Reminder
            title="Reminder"
            onDismiss={dismiss}
            body={
              <>
                <p>
                  Publishing Date means when the course appears in the learners
                  portal, and that's also the date when they receive the invitation
                  text.
                </p>
                <p className="mt-3">
                  However, they won't be able to start the course until the start
                  date (set above).
                </p>
                <p className="mt-3">
                  If you are unsure about the publishing date of some or all
                  audiences, just skip this step and come back to the configuration
                  of this course whenever you want.
                </p>
              </>
            }
          />
        }
      >
        <div
          className="rounded-2xl border-2 border-dashed border-primary-500/60 px-6 py-7"
          style={{ borderStyle: 'dashed' }}
        >
          {sameForEveryone && (
            <h4 className="text-sm font-bold text-text-primary text-center mb-3" style={{ fontFamily: FONT }}>
              Set the same date for everyone
            </h4>
          )}
          <DateField
            id={sameForEveryone ? 'publish-same' : `publish-${active.id}`}
            label="Publish date"
            value={sameForEveryone ? sameValue : active.publishDate}
            max={active.startDate}
            onChange={(v) => {
              if (sameForEveryone) setAllPublishDates(v);
              else patchAudience(active.id, { publishDate: v });
            }}
          />
          {/* The max above silently greys out later days in the native
              picker — say WHY, or admins read it as a broken calendar. */}
          {active.startDate && (
            <p className="mt-2 text-[13px] leading-5 text-text-secondary" style={{ fontFamily: FONT }}>
              The publish date must be on or before this audience&rsquo;s start
              date ({active.startDate}) — dates after it are disabled here.
              To publish later, first move the start date in Time Frames.
            </p>
          )}
        </div>
      </ReminderLayout>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function CourseConfigurePage() {
  const { id: courseId, assignmentId } = useParams<{ id: string; assignmentId?: string }>();
  const navigate = useNavigate();
  const { setCollapsed: setSidebarCollapsed } = useSidebar();

  // Route-driven mode: no param → list; "new" → create; otherwise edit a draft.
  const isList = assignmentId === undefined;
  const isCreate = assignmentId === 'new';
  const isEdit = !isList && !isCreate;

  const { data: course, isLoading: courseLoading, isError } = useCourse(courseId);
  const { data: learners = [], isLoading: learnersLoading } = useAssignableLearners();
  const { data: groups = [], isLoading: groupsLoading } = useAllGroups();
  const { data: roles = [], isLoading: rolesLoading } = useLearnerRoles();
  const { data: departments = [], isLoading: departmentsLoading } = useLearnerDepartments();
  const { data: locations = [], isLoading: locationsLoading } = useLearnerLocations();
  const { data: existingAssignments = [], isLoading: assignmentsLoading } = useAssignmentsList(courseId);
  const assignCourse = useAssignCourse();
  const updateCourseStatus = useUpdateCourse();
  const [statusPending, setStatusPending] = useState(false);

  // The same publish control the program wizard carries. A send still
  // publishes a draft on its own; this is for changing your mind either way
  // without leaving the page (Aleix, 2026-09-11).
  const changeCourseStatus = async (status: 'published' | 'draft') => {
    if (!courseId || statusPending) return;
    setStatusPending(true);
    try {
      await updateCourseStatus.mutateAsync({ courseId, data: { status } });
      toast.success(status === 'published' ? 'Course published.' : 'Course moved to drafts.');
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { error?: string; message?: string } } })
        ?.response?.data;
      if (data?.error === 'PLAN_LIMIT_REACHED') {
        setSwapLimitMessage(data.message ?? 'Your plan is at its published-course limit.');
      } else {
        toast.error(data?.message ?? 'Could not change the course status.');
      }
    } finally {
      setStatusPending(false);
    }
  };
  const revokeAssignment = useRevokeAssignment();
  const updateAssignment = useUpdateAssignment();
  // Sent-group editing (Aleix, 2026-08-23): remember the original per-learner
  // rows so save can diff — revoke removed learners, invite only new ones.
  const [editingSentRows, setEditingSentRows] = useState<
    { id: string; learner_id: string | null }[] | null
  >(null);
  // Publish-at-cap: offer the swap dialog BEFORE any invitation is sent
  // (Aleix, 2026-08-12 — invitations to a hidden course are a broken promise).
  const [swapLimitMessage, setSwapLimitMessage] = useState<string | null>(null);
  const deleteAssignment = useDeleteAssignment();
  const reduceMotion = useReducedMotion();
  const stepDelay = (i: number) => (reduceMotion ? 0 : i * 0.06);

  // Wizard scaffolding: each of the 4 steps is "active" once the course exists
  const { state, markStepSaved } = useWizardState(
    courseId ? { courseId, title: course?.title } : undefined
  );

  // Auto-collapse the dashboard sidebar (same as creation wizard)
  useEffect(() => {
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [setSidebarCollapsed]);

  // React Router doesn't reset scroll on in-app navigation. Reset manually
  // so the user lands on Step 1, not wherever the previous page was scrolled.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ── Audience state ──────────────────────────────────────────────────────
  // Start empty — the user creates their first audience explicitly. Steps 2-4
  // remain locked until at least one audience has learners. activeAudienceId
  // uses '' as the "no active" sentinel so step components can keep a simple
  // `string` prop type; their `if (!active)` guards already handle the empty
  // case naturally.
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [activeAudienceId, setActiveAudienceId] = useState<string>('');
  const [step3Same, setStep3Same] = useState(false);
  const [step4Same, setStep4Same] = useState(false);
  // Backend row ids of the draft being edited — deleted + recreated on save so
  // the learner set can change (PUT can't alter which learners are assigned).
  const [editingRowIds, setEditingRowIds] = useState<string[]>([]);

  const [learnersModalAudienceId, setLearnersModalAudienceId] = useState<string | null>(null);
  const [audiencePreviewOpen, setAudiencePreviewOpen] = useState(false);

  // Seed (or reset) the wizard from the route. A SINGLE effect owns this so a
  // reset can never clobber a seed (a separate `[assignmentId]` reset effect
  // would, under StrictMode's mount double-invoke). `seededForRef` tracks which
  // route value we've initialised so a background assignments refetch never
  // wipes the user's in-progress edits.
  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (isList) {
      seededForRef.current = null;
      return;
    }
    if (seededForRef.current === assignmentId) return;

    // Create: start blank.
    if (isCreate) {
      seededForRef.current = assignmentId!; // 'new'
      setAudiences([]);
      setActiveAudienceId('');
      setEditingRowIds([]);
      window.scrollTo(0, 0);
      return;
    }

    // Edit: wait for the list, then seed from the matching draft group.
    if (assignmentsLoading) return;
    const group = findGroupByRowId(groupAssignments(existingAssignments), assignmentId!);
    // Not found (deleted / stale id / wrong course) → return to the list
    // rather than show an empty shell. Sent groups ARE editable now: the
    // audience can grow/shrink and the schedule can move; channel & message
    // stay locked server-side (Aleix, 2026-08-23).
    if (!group) {
      navigate(`/courses/${courseId}/configure`, { replace: true });
      return;
    }
    setEditingSentRows(
      group.is_notified
        ? group.rows.map((r) => ({ id: r.id, learner_id: r.learner_id }))
        : null
    );
    const learnerIds = new Set(
      group.rows.map((r) => r.learner_id).filter((x): x is string => Boolean(x))
    );
    setAudiences([
      {
        id: `aud-edit-${assignmentId}`,
        name: group.title ?? '',
        learnerIds,
        groupIds: new Set(),
        startDate: group.start_date ?? '',
        endDate: group.end_date ?? '',
        publishDate: group.publish_date ?? '',
        invitationChannels: group.channels && group.channels.length
          ? group.channels
          : group.channel
            ? [group.channel]
            : ['EMAIL'],
        invitationHtml: group.invite_message ?? '',
      },
    ]);
    setActiveAudienceId(`aud-edit-${assignmentId}`);
    setEditingRowIds(group.rows.map((r) => r.id));
    seededForRef.current = assignmentId!;
    window.scrollTo(0, 0);
  }, [isList, isCreate, assignmentId, assignmentsLoading, existingAssignments, courseId, navigate]);

  const addAudience = useCallback(() => {
    setAudiences((prev) => {
      const n = newAudience();
      setActiveAudienceId(n.id);
      return [...prev, n];
    });
  }, []);

  const patchAudience = useCallback((id: string, patch: Partial<Audience>) => {
    setAudiences((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  const renameAudience = useCallback((id: string, name: string) => {
    setAudiences((prev) => prev.map((a) => (a.id === id ? { ...a, name } : a)));
  }, []);

  // Removing someone from a draft audience used to mean reopening the picker
  // and hunting for the ticked box (Aleix, 2026-09-08) — the card now does it
  // in one click. Sets are copied, never mutated.
  const removeAudienceLearner = useCallback((audienceId: string, learnerId: string) => {
    setAudiences((prev) =>
      prev.map((a) => {
        if (a.id !== audienceId) return a;
        const learnerIds = new Set(a.learnerIds);
        learnerIds.delete(learnerId);
        return { ...a, learnerIds };
      })
    );
  }, []);

  const removeAudienceGroup = useCallback((audienceId: string, groupId: string) => {
    setAudiences((prev) =>
      prev.map((a) => {
        if (a.id !== audienceId) return a;
        const groupIds = new Set(a.groupIds);
        groupIds.delete(groupId);
        return { ...a, groupIds };
      })
    );
  }, []);

  const removeAudience = useCallback((id: string) => {
    setAudiences((prev) => {
      const next = prev.filter((a) => a.id !== id);
      // If the active audience was just removed, fall back to the first
      // remaining one (or '' when the list is empty).
      setActiveAudienceId((current) => (current === id ? next[0]?.id ?? '' : current));
      return next;
    });
  }, []);

  const setAllPublishDates = useCallback((date: string) => {
    setAudiences((prev) => prev.map((a) => ({ ...a, publishDate: date })));
  }, []);

  // Make sure activeAudienceId always points to a real audience when one
  // exists (covers the empty → first-add case where activeAudienceId is '').
  useEffect(() => {
    if (audiences.length === 0) return;
    if (!audiences.some((a) => a.id === activeAudienceId)) {
      setActiveAudienceId(audiences[0].id);
    }
  }, [audiences, activeAudienceId]);

  // ── Scroll-driven focus tracking (matches creation wizard) ──────────────
  const [focusedStep, setFocusedStep] = useState<WizardStepNumber>(1);
  const stepRefs = useRef<Record<WizardStepNumber, HTMLDivElement | null>>({
    1: null, 2: null, 3: null, 4: null,
  });
  const setStepRef = useCallback(
    (step: WizardStepNumber) => (el: HTMLDivElement | null) => {
      stepRefs.current[step] = el;
    },
    []
  );

  useEffect(() => {
    // Focus the step whose vertical midpoint is closest to the viewport
    // center — robust at every scroll position and on resize.
    const handleScroll = () => {
      const refs = stepRefs.current;
      const viewportCenter = window.innerHeight / 2;
      let best: WizardStepNumber = 1;
      let bestDistance = Infinity;
      for (const step of [1, 2, 3, 4] as WizardStepNumber[]) {
        const el = refs[step];
        if (!el) continue;
        const { top, bottom } = el.getBoundingClientRect();
        const stepCenter = (top + bottom) / 2;
        const distance = Math.abs(stepCenter - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = step;
        }
      }
      setFocusedStep(best);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // ── Step gating ─────────────────────────────────────────────────────────
  // Steps 2/3/4 only become interactive once at least one audience has been
  // populated with learners or groups. This mirrors the create-wizard's
  // "complete Step 1 first" gate.
  const audienceReady = useMemo(
    () => audiences.some((a) => a.learnerIds.size > 0 || a.groupIds.size > 0),
    [audiences]
  );

  // ── Final save: call /assign per audience ───────────────────────────────
  const handleFinalSave = async (send: boolean) => {
    if (!courseId) return;
    if (audiences.length === 0) {
      toast.error('Add at least one audience first.');
      return;
    }
    // Sending invitations to a draft is a silent dead end for learners — so a
    // send PUBLISHES FIRST. If the plan's published-course cap blocks it, the
    // swap dialog opens and NO invitation goes out until the course can
    // actually be seen (Aleix, 2026-08-12).
    if (send && course?.status === 'archived') {
      toast.error(
        'This course is archived — learners cannot open it. Restore it to '
        + 'drafts and publish it before sending invitations.'
      );
      return;
    }
    if (send && course?.status === 'draft') {
      const hasContent = (course.sections ?? []).some(
        (s) => (s.modules ?? []).length > 0
      );
      if (!hasContent) {
        const proceed = window.confirm(
          'This course has no content yet — learners would open it and find '
          + 'nothing to learn. Publish and send the invitations anyway?'
        );
        if (!proceed) return;
      }
      try {
        await updateCourseStatus.mutateAsync({ courseId, data: { status: 'published' } });
      } catch (err) {
        const data = (err as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data;
        if (data?.error === 'PLAN_LIMIT_REACHED') {
          setSwapLimitMessage(data.message ?? 'Your plan is at its published-course limit.');
        } else {
          toast.error(data?.message ?? 'Could not publish the course. Nothing was sent.');
        }
        return; // never send invitations to a course that stayed hidden
      }
    }
    // Walk audiences and surface the first specific issue. A draft only needs
    // learners; dates become mandatory the moment you send.
    const aLabel = (a: Audience, i: number) => audienceDisplayName(a, i);
    for (const [i, a] of audiences.entries()) {
      if (a.learnerIds.size === 0 && a.groupIds.size === 0) {
        toast.error(`${aLabel(a, i)} needs at least one learner.`);
        return;
      }
      if (send) {
        const missing: string[] = [];
        if (!a.startDate) missing.push('a start date');
        if (!a.endDate) missing.push('an end date');
        if (missing.length > 0) {
          toast.error(`${aLabel(a, i)} is missing ${missing.join(' & ')}.`);
          return;
        }
      }
      // Whenever both dates are present, keep them consistent (draft or send).
      if (a.startDate && a.endDate && new Date(a.endDate) <= new Date(a.startDate)) {
        toast.error(`${aLabel(a, i)}: end date must be after the start date.`);
        return;
      }
      // Strictly-after is the error: publishing ON the start date is allowed
      // ("on or before"), matching the modal, programs page, and backend schema.
      if (a.publishDate && a.startDate && new Date(a.publishDate) > new Date(a.startDate)) {
        toast.error(`${aLabel(a, i)}: Publish date must be on or before the start date.`);
        return;
      }
    }
    try {
      // ── Sent-group editing: diff, never delete-and-recreate ──────────────
      // Removed learners are revoked (access closes, history stays); new
      // learners get a fresh send of their own; schedule/title changes PATCH
      // the remaining rows. Channel & message are locked server-side.
      if (isEdit && editingSentRows) {
        const aud = audiences[0];
        if (!aud) return;
        const original = new Map(
          editingSentRows.filter((r) => r.learner_id).map((r) => [r.learner_id as string, r.id])
        );
        const selected = aud.learnerIds;
        const removed = [...original.keys()].filter((lid) => !selected.has(lid));
        const added = [...selected].filter((lid) => !original.has(lid));

        if (removed.length > 0) {
          const ok = window.confirm(
            `Remove ${removed.length} learner${removed.length === 1 ? '' : 's'} from this course? `
            + 'They lose access immediately; their progress and history are kept. '
            + 'The invitation they already received cannot be unsent.'
          );
          if (!ok) return;
          for (const lid of removed) {
            await revokeAssignment.mutateAsync({
              courseId,
              assignmentId: original.get(lid) as string,
            });
          }
        }

        if (added.length > 0) {
          await assignCourse.mutateAsync({
            courseId,
            data: {
              learner_ids: added,
              group_ids: null,
              title: aud.name.trim() || undefined,
              start_date: aud.startDate || undefined,
              end_date: aud.endDate || undefined,
              publish_date: aud.publishDate ? `${aud.publishDate}T00:00:00Z` : undefined,
              channels: aud.invitationChannels,
              invite_message: aud.invitationHtml || undefined,
              send,
            },
          });
        }

        // Schedule/title updates for the learners who stay.
        const remainingRowIds = [...original.entries()]
          .filter(([lid]) => selected.has(lid))
          .map(([, rowId]) => rowId);
        await Promise.all(
          remainingRowIds.map((rowId) =>
            updateAssignment.mutateAsync({
              courseId,
              assignmentId: rowId,
              data: {
                title: aud.name.trim() || null,
                start_date: aud.startDate || undefined,
                end_date: aud.endDate || undefined,
                publish_date: aud.publishDate ? `${aud.publishDate}T00:00:00Z` : null,
              },
            })
          )
        );

        const parts = [
          added.length && `${added.length} invited`,
          removed.length && `${removed.length} removed`,
          remainingRowIds.length && 'schedule updated',
        ].filter(Boolean);
        toast.success(`Invitation updated — ${parts.join(', ')}.`);
        navigate(`/courses/${courseId}/configure`);
        return;
      }

      // Editing a draft = replace it: drop the original rows, then recreate
      // from the (possibly modified) wizard audiences. Safe because drafts are
      // never notified, so the delete guard never trips.
      if (isEdit && editingRowIds.length > 0) {
        await Promise.all(
          editingRowIds.map((id) =>
            deleteAssignment.mutateAsync({ courseId, assignmentId: id })
          )
        );
        setEditingRowIds([]);
      }
      let publishedAny = false;
      let notifiedTotal = 0;
      for (const aud of audiences) {
        const result = await assignCourse.mutateAsync({
          courseId,
          data: {
            learner_ids: aud.learnerIds.size > 0 ? Array.from(aud.learnerIds) : null,
            group_ids: aud.groupIds.size > 0 ? Array.from(aud.groupIds) : null,
            // Optional name; omit when blank so the row stores a null title and
            // the landing renders the dated default.
            title: aud.name.trim() || undefined,
            // Dates are optional for a draft; omit empty so the backend stores null.
            start_date: aud.startDate || undefined,
            end_date: aud.endDate || undefined,
            // Backend expects ISO-8601 datetime for publish_date; the HTML
            // <input type="date"> emits YYYY-MM-DD so anchor it to UTC midnight.
            publish_date: aud.publishDate ? `${aud.publishDate}T00:00:00Z` : undefined,
            channels: aud.invitationChannels,
            invite_message: aud.invitationHtml || undefined,
            send,
          },
        });
        publishedAny = publishedAny || result.auto_published;
        notifiedTotal += result.notified ?? 0;
      }
      const audCount = audiences.length;
      if (send) {
        toast.success(
          `Assigned to ${audCount} ${audCount === 1 ? 'audience' : 'audiences'} — ${notifiedTotal} ${notifiedTotal === 1 ? 'learner' : 'learners'} notified.`
        );
      } else {
        toast.success(
          `Saved ${audCount} draft ${audCount === 1 ? 'invitation' : 'invitations'} — not yet sent.`
        );
      }
      if (publishedAny) {
        toast('Course auto-published — visible to assigned learners.', { icon: '✨' });
      }
      markStepSaved(4);
      // Return to the assignments list so the freshly-saved configs appear.
      navigate(`/courses/${courseId}/configure`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } }).response?.status;
      const code = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      if (status === 501 && code === 'CHANNEL_NOT_IMPLEMENTED') {
        toast.error("Slack and Tereiko chat aren't available yet. Switch to Email and try again.");
        return;
      }
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to assign the course. Please try again.';
      toast.error(message);
    }
  };

  // ── Loading / error states ──
  if (courseLoading) {
    return (
      <div className="p-6 lg:p-10 max-w-[1080px] mx-auto">
        <div className="h-6 w-32 rounded bg-text-tertiary/10 animate-pulse mb-8" />
        <div className="h-32 rounded-2xl bg-text-tertiary/10 animate-pulse mb-6" />
      </div>
    );
  }
  if (isError || !course) {
    return (
      <div className="p-6 lg:p-10 max-w-[640px] mx-auto text-center">
        <p className="text-sm text-error-500 mb-4">Could not load this course.</p>
        <PillButton variant="ghost" onClick={() => navigate('/courses')}>
          ← Back to Courses
        </PillButton>
      </div>
    );
  }

  // Landing: list of existing assignment configs + CTA. "New" / "Edit" route
  // to the wizard at a dedicated URL.
  if (isList) {
    return (
      <CourseAssignmentsLanding
        courseId={courseId!}
        courseTitle={course.title}
        onCreateNew={() => navigate(`/courses/${courseId}/configure/new`)}
        onEdit={(rowId) => navigate(`/courses/${courseId}/configure/${rowId}`)}
        onBack={() => navigate('/courses')}
      />
    );
  }

  // Edit mode: hold the wizard until the draft is fetched + seeded so we never
  // flash an empty shell. The seeding effect redirects to the list if the draft
  // can't be found, in which case this loader shows until the redirect lands.
  if (isEdit && seededForRef.current !== assignmentId) {
    return (
      <div className="p-6 lg:p-10 max-w-[1080px] mx-auto">
        <div className="h-6 w-40 rounded bg-text-tertiary/10 animate-pulse mb-8" />
        <div className="h-48 rounded-2xl bg-text-tertiary/10 animate-pulse" />
      </div>
    );
  }

  const learnersModalAudience = audiences.find((a) => a.id === learnersModalAudienceId) ?? null;

  return (
    <div className="min-h-screen">
      <Toaster position="top-right" />

      {swapLimitMessage && course && (
        <PublishSwapDialog
          courseId={courseId!}
          courseTitle={course.title}
          limitMessage={swapLimitMessage}
          onClose={() => setSwapLimitMessage(null)}
          onSwapped={() => {
            setSwapLimitMessage(null);
            // The course is published now — resume the send that was blocked.
            void handleFinalSave(true);
          }}
        />
      )}

      <WizardStepIndicator
        steps={state.steps}
        focusedStep={focusedStep}
        gamificationEnabled={true}
        labels={STEP_LABELS}
      />

      {/* Page header strip */}
      <div className="max-w-[1080px] mx-auto px-6 pt-6 flex items-center justify-between">
        <PillButton variant="ghost" onClick={() => navigate(`/courses/${courseId}/configure`)}>
          ← Back to assignments
        </PillButton>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <p className="text-sm text-text-secondary truncate max-w-[60ch]" style={{ fontFamily: FONT }}>
            {isEdit ? 'Editing assignment for' : 'Configuring'}{' '}
            <span className="font-bold text-text-primary">{course.title}</span>
          </p>
          {/* Same control as the program wizard — these two screens move
              together (Aleix, 2026-09-11). */}
          <PublishStatusControl
            entity="course"
            status={course.status as PublishableStatus}
            pending={statusPending}
            onPublish={() => changeCourseStatus('published')}
            onUnpublish={() => changeCourseStatus('draft')}
          />
        </div>
      </div>

      {editingSentRows && (
        <div className="max-w-[1080px] mx-auto px-6 pt-4">
          <div className="rounded-xl border border-secondary-500/40 bg-secondary-500/[0.07] px-4 py-3 text-sm text-text-primary">
            <span className="font-semibold">Editing a sent invitation.</span>{' '}
            You can add learners (only they will be invited), remove learners
            (they lose access; their history is kept), and change the schedule.
            The invitation channel and message were already delivered and are
            locked.
          </div>
        </div>
      )}

      <div className="max-w-[1080px] mx-auto px-6 pt-8 pb-24 flex flex-col gap-10">
        {/* Step 1: Audiences */}
        <div ref={setStepRef(1)} data-configure-step="1">
          <StepCard
            stepNumber={1}
            title="Audiences"
            entranceDelay={stepDelay(0)}
          >
            <Step1Audiences
              audiences={audiences}
              learners={learners}
              groups={groups}
              onAddAudience={addAudience}
              onUpdateAudience={(id) => setActiveAudienceId(id)}
              onAddLearners={(id) => setLearnersModalAudienceId(id)}
              onRenameAudience={renameAudience}
              onRemoveAudience={removeAudience}
              onRemoveLearner={removeAudienceLearner}
              onRemoveGroup={removeAudienceGroup}
            />
          </StepCard>
        </div>

        {/* Step 2: Time Frames */}
        <div ref={setStepRef(2)} data-configure-step="2" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard
            stepNumber={2}
            title="Time Frames"
            disabled={!audienceReady}
            entranceDelay={stepDelay(1)}
          >
            <Step2TimeFrames
              audiences={audiences}
              activeId={activeAudienceId}
              setActiveId={setActiveAudienceId}
              patchAudience={patchAudience}
            />
          </StepCard>
        </div>

        {/* Step 3: Invitation Text */}
        <div ref={setStepRef(3)} data-configure-step="3" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard
            stepNumber={3}
            title="Compose an Invitation Text"
            disabled={!audienceReady}
            entranceDelay={stepDelay(2)}
          >
            <Step3Invitation
              audiences={audiences}
              activeId={activeAudienceId}
              setActiveId={setActiveAudienceId}
              patchAudience={patchAudience}
              sameForEveryone={step3Same}
              setSameForEveryone={setStep3Same}
            />
          </StepCard>
        </div>

        {/* Step 4: Publishing — actions live in the bottom toolbar */}
        <div ref={setStepRef(4)} data-configure-step="4" className="relative">
          {!audienceReady && <LockedOverlay message="Add learners to an audience first" />}
          <StepCard
            stepNumber={4}
            title="Publishing"
            disabled={!audienceReady}
            entranceDelay={stepDelay(3)}
          >
            <Step4Publishing
              audiences={audiences}
              activeId={activeAudienceId}
              setActiveId={setActiveAudienceId}
              patchAudience={patchAudience}
              sameForEveryone={step4Same}
              setSameForEveryone={setStep4Same}
              setAllPublishDates={setAllPublishDates}
            />
          </StepCard>
        </div>

        {/* ── Single bottom action bar ─────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <PillButton variant="ghost" onClick={() => navigate(`/courses/${courseId}/configure`)}>
            <XIcon size={14} /> Cancel
          </PillButton>
          <div className="flex items-center gap-3 flex-wrap justify-center">
            <PillButton
              variant="orange-outline"
              onClick={() => setAudiencePreviewOpen(true)}
              disabled={audiences.length === 0}
            >
              <EyeOpenIcon /> Preview
            </PillButton>
            <PillButton
              variant="ghost"
              onClick={() => handleFinalSave(false)}
              disabled={!audienceReady || assignCourse.isPending || deleteAssignment.isPending}
            >
              <PenIcon /> Save as draft
            </PillButton>
            <PillButton
              variant="purple"
              onClick={() => handleFinalSave(true)}
              disabled={!audienceReady || assignCourse.isPending || deleteAssignment.isPending}
            >
              <CheckIcon size={14} />
              {assignCourse.isPending ? 'Saving…' : 'Save & send'}
            </PillButton>
          </div>
        </div>
      </div>

      {/* Add Learners modal */}
      <AddLearnersModal
        isOpen={learnersModalAudience !== null}
        onClose={() => setLearnersModalAudienceId(null)}
        audience={learnersModalAudience}
        learners={learners}
        groups={groups}
        roles={roles}
        departments={departments}
        locations={locations}
        loading={learnersLoading || groupsLoading}
        rolesLoading={rolesLoading}
        departmentsLoading={departmentsLoading}
        locationsLoading={locationsLoading}
        onApply={(next) =>
          learnersModalAudience &&
          patchAudience(learnersModalAudience.id, {
            learnerIds: next.learnerIds,
            groupIds: next.groupIds,
          })
        }
      />

      {/* Audience Preview modal — Step 1 onPreview */}
      <AudiencePreviewModal
        isOpen={audiencePreviewOpen}
        onClose={() => setAudiencePreviewOpen(false)}
        audiences={audiences}
        learners={learners}
        groups={groups}
      />
    </div>
  );
}
